/**
 * ProjectHero · W124.2 · 2026-05-26
 *
 * Header sticky único pro shell de obra (`/projects/:id/*`). Substitui o
 * bloco antigo que ocupava ~60% da viewport em smoke (W124 screenshot)
 * com:
 *   • back-link "Todas as obras" (chevron)
 *   • nome da obra + StatusBadge
 *   • botão "?" Ajuda (abre AjudaContextual quando há contexto publicado)
 *   • menu "⋯" admin-only com ações de manutenção (refresh geo, etc.)
 *
 * O QUE NÃO ESTÁ AQUI (intencional — movidas pra outras superfícies):
 *   • endereço completo → tooltip do nome / Mesa Plano (aba Sobre)
 *   • "Relatório do mês" → `/projects/:id/entrega?tab=relatorio`
 *   • progress bar 0% → sub-header em `/hoje`
 *   • tab strip antigo (Visão geral/Etapas/...) → DELETADO (substituído
 *     pelo `ProjectTabBar` pin/dynamic do W349/W350 que vive ABAIXO do
 *     hero).
 *   • mapa interativo (`ProjectGeoPanel`) → renderizado em /visao-geral
 *     pela page; não é mais parte do hero.
 *
 * Z-index: 20 — abaixo do header do shell (TopBar/MobileProjectHeader, 30),
 * do PWA prompt (40) e do wizard footer (50). O hero é sticky em top-0 do
 * container de scroll interno do AppShell (que já começa abaixo do header
 * global); top-14/md:top-16 empurrava o hero pra baixo SOBRE o primeiro card.
 * Mobile-first: tudo cabe em 375px porque o nome usa `truncate` e o
 * StatusBadge é a única ilha que cresce verticalmente.
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProjectStatus } from '@builder/shared';
import { api } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import {
  MissingFieldBadge,
  detectMissingFields,
} from '@/components/ui/MissingFieldBadge';
import { useOpenAjuda } from '@/components/ajuda/AjudaContextual';
import { useAuth } from '@/store/auth.store';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

interface ProjectHeroProps {
  projectId: string;
  name: string;
  status: ProjectStatus;
  /** Endereço completo — mostrado em tooltip do title pra orientação rápida. */
  address?: string | null;
  /**
   * W126.1 · 2026-05-26 — campos opcionais pra detectar fallback
   * "A definir" do wizard finalize. Quando passados, renderiza
   * MissingFieldBadge ao lado do StatusBadge. Compatibilidade: callers
   * antigos (sem clientName/tipoObra) continuam funcionando sem badge.
   */
  clientName?: string | null;
  tipoObra?: string | null;
}

export function ProjectHero({
  projectId,
  name,
  status,
  address,
  clientName,
  tipoObra,
}: ProjectHeroProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const openAjuda = useOpenAjuda();
  const user = useAuth((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  return (
    <header
      data-testid="project-hero"
      className={cn(
        // L-OVERLAP fix · 2026-06-08 — o conteúdo do AppShell rola num
        // container interno (overflow-y:auto) que JÁ começa abaixo do header
        // global. Logo o hero gruda em top-0 DESSE container (e fica logo
        // abaixo do header global), não top-14. O top-14/md:top-16 antigo
        // (offset herdado de quando o scroll era a página) empurrava o hero
        // 56–64px pra baixo, SOBREPONDO o primeiro card ("Próxima ação") —
        // que aparecia cortado no topo. top-0 reserva o espaço corretamente.
        // backdrop-blur evita ficar "colado" no conteúdo abaixo.
        'sticky top-0 z-20',
        // M1 fix · 2026-06-03 — bg was bg-surface/95 (95% opaque), so content
        // scrolling under the sticky bar BLED THROUGH it and the first card
        // ("Próxima ação" / stepper) looked clipped/garbled. Full opacity
        // (bg-surface, no /95) hides scrolled-under content cleanly like a
        // normal sticky header; backdrop-blur kept for the frosted edge.
        // (scroll-padding-top in globals.css handles anchor/focus jumps.)
        'bg-surface backdrop-blur',
        'border-b border-line',
        'px-4 py-3',
        'flex items-center gap-3',
      )}
    >
      <Link
        to="/projects"
        aria-label={isEn ? 'All projects' : 'Todas as obras'}
        title={isEn ? 'All projects' : 'Todas as obras'}
        className="pressable inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-surface-2 hover:text-ink"
      >
        <svg
          aria-hidden="true"
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </Link>

      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
        <h1
          className="text-base font-semibold text-ink truncate"
          title={address ? `${name} · ${address}` : name}
        >
          {name}
        </h1>
        <StatusBadge status={status} />
        {/* W126.1 · 2026-05-26 — quando o wizard finalize persistiu
            fallback "A definir" pra address/clientName ou null em
            tipoObra, mostramos badge inline ao lado do status. Mantém
            o hero compacto (sem nova linha) e oferece deep link de
            correção. Só renderiza quando há algo faltando. */}
        {(() => {
          const missing = detectMissingFields({
            address,
            clientName,
            tipoObra,
          });
          if (!missing.length) return null;
          return (
            <MissingFieldBadge
              fields={missing}
              projectId={projectId}
              data-testid="project-hero-missing-fields"
            />
          );
        })()}
      </div>

      {openAjuda && (
        <button
          type="button"
          onClick={openAjuda}
          aria-label={isEn ? 'Help' : 'Ajuda'}
          title={isEn ? 'Help' : 'Ajuda'}
          data-testid="project-hero-ajuda"
          className="pressable inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-surface-2 hover:text-ink"
        >
          <svg
            aria-hidden="true"
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx={12} cy={12} r={10} />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1={12} y1={17} x2={12.01} y2={17} />
          </svg>
        </button>
      )}

      {isAdmin && (
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((p) => !p)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Ações de administrador"
            title="Ações"
            data-testid="project-hero-admin-menu"
            className="pressable inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-surface-2 hover:text-ink"
          >
            <svg
              aria-hidden="true"
              width={20}
              height={20}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx={5} cy={12} r={1} />
              <circle cx={12} cy={12} r={1} />
              <circle cx={19} cy={12} r={1} />
            </svg>
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-30 mt-1 w-56 rounded-md border border-line bg-surface shadow-lg"
            >
              <AdminGeoRefreshItem
                projectId={projectId}
                onDone={() => setMenuOpen(false)}
              />
            </div>
          )}
        </div>
      )}
    </header>
  );
}

function AdminGeoRefreshItem({
  projectId,
  onDone,
}: {
  projectId: string;
  onDone: () => void;
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api.updateProject(projectId, {}),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
  return (
    <button
      type="button"
      role="menuitem"
      data-testid="project-hero-admin-geo-refresh"
      onClick={() => {
        refresh.mutate(undefined, { onSettled: onDone });
      }}
      disabled={refresh.isPending}
      className="pressable block w-full px-3 py-2 text-left text-[12.5px] text-ink hover:bg-surface-2 disabled:opacity-60"
    >
      {refresh.isPending
        ? (isEn ? 'Updating…' : 'Atualizando…')
        : (isEn ? 'Refresh address geo' : 'Atualizar geo deste endereço')}
    </button>
  );
}

export default ProjectHero;
